import React, { useState, useEffect, useRef } from 'react';
import { PostRequest, GetRequest } from 'components/common/index.js';
import { ContentPopup } from 'components/ContentPopup.jsx';
import AttributePropertyTags from 'components/quote_builder/attributePropertyTags.jsx';
import styles from 'components/quote_builder_widget/settings/_style.module.scss';

const translate = (key) => t(`quote_builder_widget.${key}`);

// The machines of one kind (machineKey: printing_machine, hot_stamping_machine) the widget prices on. The owner adds a
// machine as they add a material -- materials first: only the machines the widget's materials allow are offered
// (supported_by_widget_materials). Each machine is shown as the Manufacturing Cost Calculator settings list shows it
// (SettingsUserUnitPriceAttributeList): its name and the same property pills, read through that machine's settings
// configuration. labels names the quote_builder_widget translation key of each text; machineLabel the machine's own
// name, which titles the added machines until the settings configuration arrives.
const WidgetMachinesSection = ({ machineKey, listUrl, listField, labels, machineLabel, onMachinesChange }) => {
  const [machines, setMachines] = useState([]);
  const [widgetMaterials, setWidgetMaterials] = useState(false);
  const [settingsConfiguration, setSettingsConfiguration] = useState(null);
  const [machineFormConfiguration, setMachineFormConfiguration] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [updatingId, setUpdatingId] = useState(null);
  const [showAddPopup, setShowAddPopup] = useState(false);
  const addPopupRef = useRef();

  const fetchMachines = () => {
    GetRequest({
      url: listUrl,
      callback: (data) => {
        setMachines(data[listField]);
        setWidgetMaterials(data.widget_materials);
        setLoading(false);
      },
      // Any failure -- an error status, an expired session, a dropped connection -- ends the loading state.
      onError: () => {
        setLoading(false);
        setErrorMessage(translate(labels.failedToLoad));
      }
    });
  };

  const fetchConfigurations = () => {
    GetRequest({
      url: `/quote-builder/config/settings/user_unit_prices/${machineKey}`,
      useCache: true,
      callback: (data) => { setSettingsConfiguration(data); }
    });
    GetRequest({
      url: `/quote-builder/user_unit_price_attributes/config/${machineKey}/${machineKey}`,
      callback: (data) => { setMachineFormConfiguration(data); }
    });
  };

  useEffect(() => {
    fetchMachines();
    fetchConfigurations();
  }, []);

  const tabConfiguration = (settingsConfiguration?.tabs || []).find((tab) => tab.form_type === 'UserUnitPriceAttribute');

  const closeAddPopup = () => {
    setShowAddPopup(false);
    if (addPopupRef.current) {
      addPopupRef.current.style.display = 'none';
    }
  };

  const updateWidgetStatus = (machine, widgetEnabled) => {
    setUpdatingId(machine.id);
    setErrorMessage('');
    PostRequest({
      url: `/quote-builder/user_unit_price_attributes/${machine.id}/update-widget-status`,
      data: { widget_enabled: widgetEnabled ? 'true' : 'false' },
      callback: () => {
        setUpdatingId(null);
        closeAddPopup();
        fetchMachines();
        if (onMachinesChange) {
          onMachinesChange();
        }
      },
      400: (data) => {
        setUpdatingId(null);
        setErrorMessage(data?.error || translate(labels.failedToUpdate));
      },
      404: () => {
        setUpdatingId(null);
        setErrorMessage(translate(labels.failedToUpdate));
      },
      500: () => {
        setUpdatingId(null);
        setErrorMessage(translate('server_error'));
      }
    });
  };

  const renderErrorMessage = () => {
    if (!errorMessage) {
      return null;
    }
    return (
      <div style={{ color: '#dc3545', marginBottom: '10px', padding: '8px', backgroundColor: '#f8d7da', borderRadius: '4px' }}>
        {errorMessage}
      </div>
    );
  };

  const addedMachines = machines.filter((machine) => machine.widget_enabled);
  const addableMachines = machines.filter((machine) => machine.supported_by_widget_materials && !machine.widget_enabled);

  const noAddableMachinesMessage = () => {
    if (!widgetMaterials) { return translate(labels.noWidgetMaterials); }
    if (machines.length === 0) { return translate(labels.noMachines); }
    return translate(labels.noMachinesForMaterials);
  };

  // The settings list's machine row: the name and its property pills, the action on the right.
  const machineRow = (machine, action) => (
    <tr key={machine.id} data-testid="widget-machine">
      <td>
        <strong>{machine.name}</strong>
        {machine.widget_enabled && !machine.supported_by_widget_materials && (
          <div data-testid="widget-machine-not-used" style={{ color: '#dc3545', fontSize: '12px' }}>
            {translate(labels.notUsed)}
          </div>
        )}
        <AttributePropertyTags
          properties={machine.properties}
          listingProperties={tabConfiguration?.listing_properties}
          subFormAttributes={machineFormConfiguration?.create_sub_form?.attributes}
          sectionAttributes={settingsConfiguration?.attributes}
        />
      </td>
      <td>
        <div className="float-right" style={{ whiteSpace: 'nowrap' }}>{action}</div>
      </td>
    </tr>
  );

  const machinesTable = (title, rows) => (
    <table className="table table-striped table-bordered-outside" style={{ marginTop: '10px' }}>
      <tbody>
        <tr>
          <th colSpan="2">
            <div className="float-left">{title}</div>
          </th>
        </tr>
        {rows}
      </tbody>
    </table>
  );

  const renderAddedMachines = () => {
    if (addedMachines.length === 0) {
      return (
        <div style={{ color: '#666', fontStyle: 'italic', marginBottom: '10px' }}>
          {translate(labels.noneAdded)}
        </div>
      );
    }

    return machinesTable(
      `${tabConfiguration ? t(tabConfiguration.label) : t(machineLabel)} (${addedMachines.length})`,
      addedMachines.map((machine) => machineRow(machine, (
        <a role="button" style={{ cursor: 'pointer' }} onClick={() => updateWidgetStatus(machine, false)}>
          <i className="fa fa-trash-o" aria-hidden="true"></i>{' '}
          {updatingId === machine.id ? translate('removing') : translate('remove')}
        </a>
      )))
    );
  };

  const renderAddPopupContent = () => (
    <div style={{ padding: '20px', minWidth: '500px' }}>
      <h4>{translate(labels.add)}</h4>
      {renderErrorMessage()}
      {addableMachines.length === 0 ? (
        <p>{noAddableMachinesMessage()}</p>
      ) : (
        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
          {machinesTable(
            translate(labels.select),
            addableMachines.map((machine) => machineRow(machine, (
              <a role="button" style={{ cursor: 'pointer' }} onClick={() => updateWidgetStatus(machine, true)}>
                <i className="fa fa-plus-square-o" aria-hidden="true"></i>{' '}
                {translate('add_to_widget')}
              </a>
            )))
          )}
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div style={{ padding: '10px', color: '#666' }}>
        {translate('loading')}
      </div>
    );
  }

  return (
    <div className={styles.fieldGroup}>
      <p style={{ color: '#666' }}>{translate(labels.information)}</p>
      {!showAddPopup && renderErrorMessage()}
      {renderAddedMachines()}
      <button type="button" className="btn simple-btn" onClick={() => setShowAddPopup(true)}>
        <i className="fa fa-plus-square-o" aria-hidden="true"></i>{' '}
        {translate(labels.add)}
      </button>
      {showAddPopup && (
        <ContentPopup
          myRef={addPopupRef}
          autoOpen={true}
          content={renderAddPopupContent()}
          cancelButton={false}
          beforeClose={() => {
            setShowAddPopup(false);
            setErrorMessage('');
          }}
        />
      )}
    </div>
  );
};

export default WidgetMachinesSection;
